
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";
import dayjs from "dayjs";
import { AoAvatar } from "@26lights/orcha";

import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";
import {
  ADD_ENTITY_COMMENT,
  UPDATE_ENTITY_COMMENT,
  DELETE_ENTITY_COMMENT,
} from "@/graphql/comments/mutations";
import { GET_ENTITY_COMMENTS } from "@/graphql/comments/queries";

interface CommentUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

interface EntityComment {
  id: string;
  entityType: string;
  entityId: string;
  message: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  isAuthor: boolean;
  user?: CommentUser | null;
}

export default defineComponent({
  components: {
    Modal,
    BasicButton,
    AoAvatar,
  },
  props: {
    entityType: {
      type: String as PropType<string>,
      required: true,
    },
    entityId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  emits: ["close", "changed"],
  setup(props, { emit }) {
    const { t } = useI18n({
      useScope: "global",
      inheritLocale: true,
    });
    const toast = useToast();

    const newComment = ref("");
    const editingCommentId = ref<string | null>(null);
    const editingMessage = ref("");

    const { result, loading, refetch, onError } = useQuery(
      GET_ENTITY_COMMENTS,
      {
        entityType: props.entityType,
        entityId: props.entityId,
      },
    );

    onError((error) => {
      logErrorMessages(error);
      toast.error(t("comments.errors.get"));
    });

    const comments = computed<EntityComment[]>(() => {
      return result.value?.entityComments ?? [];
    });

    const { mutate: addEntityComment } = useMutation(ADD_ENTITY_COMMENT);
    const { mutate: updateEntityComment } = useMutation(UPDATE_ENTITY_COMMENT);
    const { mutate: deleteEntityComment } = useMutation(DELETE_ENTITY_COMMENT);

    const handleSubmit = async () => {
      const message = newComment.value.trim();

      if (!message) return;

      try {
        const response = await addEntityComment({
          entityType: props.entityType,
          entityId: props.entityId,
          message,
        });

        const error = response?.data?.addEntityComment?.error;

        if (error) {
          toast.error(t("comments.errors.add"));
          return;
        }

        newComment.value = "";
        toast.success(t("comments.success.add"));
        emit("changed");
        void refetch();
      } catch (error) {
        logErrorMessages(error);
        toast.error(t("comments.errors.add"));
      }
    };

    const startEditing = (comment: EntityComment) => {
      editingCommentId.value = comment.id;
      editingMessage.value = comment.message;
    };

    const cancelEditing = () => {
      editingCommentId.value = null;
      editingMessage.value = "";
    };

    const saveComment = async (commentId: string) => {
      const message = editingMessage.value.trim();

      if (!message) return;

      try {
        const response = await updateEntityComment({
          id: commentId,
          message,
        });

        const error = response?.data?.updateEntityComment?.error;

        if (error) {
          toast.error(t("comments.errors.update"));
          return;
        }

        cancelEditing();
        toast.success(t("comments.success.update"));
        void refetch();
      } catch (error) {
        logErrorMessages(error);
        toast.error(t("comments.errors.update"));
      }
    };

    const removeComment = async (commentId: string) => {
      try {
        const response = await deleteEntityComment({
          id: commentId,
        });

        const error = response?.data?.deleteEntityComment?.error;

        if (error) {
          toast.error(t("comments.errors.delete"));
          return;
        }

        if (editingCommentId.value === commentId) {
          cancelEditing();
        }

        toast.success(t("comments.success.delete"));
        emit("changed");
        void refetch();
      } catch (error) {
        logErrorMessages(error);
        toast.error(t("comments.errors.delete"));
      }
    };

    const formatDate = (dateStr: string) => {
      const date = dayjs(dateStr);
      const now = dayjs();

      if (date.isSame(now, "day")) {
        return `today at ${date.format("h:mm A")}`;
      }

      if (date.isSame(now.subtract(1, "day"), "day")) {
        return `yesterday at ${date.format("h:mm A")}`;
      }

      return date.format("MMM D, YYYY h:mm A");
    };

    const getUserName = (user?: CommentUser | null) => {
      if (!user) return "";

      return [user.firstName, user.lastName].filter(Boolean).join(" ");
    };

    return {
      t,
      comments,
      loading,
      newComment,
      editingCommentId,
      editingMessage,
      handleSubmit,
      startEditing,
      cancelEditing,
      saveComment,
      removeComment,
      formatDate,
      getUserName,
    };
  },
});
